"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  signInWithEmailAndPassword,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth"
import { auth, db, ref, set, get } from "../Firebase/firestore"
import "./Login.css"
import {
  FaUser,
  FaLock,
  FaBuilding,
  FaSignInAlt,
  FaExclamationTriangle,
  FaPhone,
  FaShieldAlt,
} from "react-icons/fa"

const brandMapping = {
  "ultratech@altrange.com": "Ultratech",
  "shree@altrange.com": "Shree",
  "acc@altrange.com": "ACC",
  "jsw@altrange.com": "JSW",
  "jksuper@altrange.com": "JKSuper",
  "ambuja@altrange.com": "Ambuja",
  "office@altrange.com": "super_admin",
}

const ALLOWED_NUMBER = "+919876980108" 

// Timer duration in seconds
const RESEND_OTP_TIMEOUT = 60

const Login = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const navigate = useNavigate()
  const [selectedBrand, setSelectedBrand] = useState("")
  const [showTwoFA, setShowTwoFA] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState("")
  const [otp, setOtp] = useState("")
  const [confirmationResult, setConfirmationResult] = useState(null)
  const [isOtpSent, setIsOtpSent] = useState(false)
  const [userCredential, setUserCredential] = useState(null)
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null)

  // Timer state
  const [timer, setTimer] = useState(0)

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === "Enter" && email && password && !isLoading && !showTwoFA) {
        handleLogin(e)
      }
    }
    window.addEventListener("keydown", handleEnterKey)
    return () => window.removeEventListener("keydown", handleEnterKey)
  }, [email, password, isLoading, showTwoFA])

  // initialize invisible recaptcha once
  useEffect(() => {
    if (!recaptchaVerifier) {
      try {
        const verifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          {
            size: "invisible",
            callback: () => console.log("reCAPTCHA solved"),
            "expired-callback": () => setErrorMessage("reCAPTCHA expired. Please try again."),
          }
        )
        setRecaptchaVerifier(verifier)
        window.recaptchaVerifier = verifier
      } catch (err) {
        console.error("reCAPTCHA init error:", err)
        setErrorMessage("reCAPTCHA could not be initialized.")
      }
    }

    return () => {
      // Cleanup on unmount
      try {
        if (window.recaptchaVerifier) {
          window.recaptchaVerifier.clear()
          window.recaptchaVerifier = null
        }
      } catch (e) {
        // ignore cleanup errors
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Countdown effect
  useEffect(() => {
    let interval = null
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1)
      }, 1000)
    } else if (timer === 0) {
      // when timer reaches 0, invalidate OTP state to prevent re-use
      // If an OTP was sent, mark it expired and force user to resend
      if (isOtpSent) {
        setErrorMessage("OTP expired. Please resend to get a new code.")
        setSuccessMessage("")
        setIsOtpSent(false)
        // Clear confirmationResult so `confirmationResult.confirm()` can't be called later
        try {
          setConfirmationResult(null)
        } catch (e) {
          console.error("Error clearing confirmation result:", e)
        }
      }
    }
    return () => clearInterval(interval)
  }, [timer, isOtpSent])

  const checkTwoFARequired = async (userId) => {
    try {
      const today = new Date().toDateString()
      const twoFARef = ref(db, `twoFA/${userId}`)
      const snapshot = await get(twoFARef)
      if (snapshot.exists()) {
        const data = snapshot.val()
        return data.lastVerified !== today
      }
      return true
    } catch (error) {
      console.error("Error checking 2FA status:", error)
      return true
    }
  }

  const markTwoFACompleted = async (userId) => {
    try {
      const today = new Date().toDateString()
      const twoFARef = ref(db, `twoFA/${userId}`)
      await set(twoFARef, {
        lastVerified: today,
        timestamp: Date.now(),
      })
    } catch (error) {
      console.error("Error marking 2FA completed:", error)
    }
  }

  const handleEmailChange = (e) => {
    const newEmail = e.target.value
    setEmail(newEmail)
    const expectedBrand = brandMapping[newEmail.trim().toLowerCase()]
    if (expectedBrand) {
      setSelectedBrand(expectedBrand)
      setErrorMessage("")
    } else {
      setSelectedBrand("")
    }
  }

  const handleBrandChange = (e) => setSelectedBrand(e.target.value)

  const handleLogin = async (event) => {
    event.preventDefault()
    setIsLoading(true)
    setErrorMessage("")
    setSuccessMessage("")

    const expectedBrand = brandMapping[email.trim().toLowerCase()]
    if (!expectedBrand) {
      setErrorMessage("This email is not registered as an admin.")
      setIsLoading(false)
      return
    }

    if (selectedBrand !== expectedBrand) {
      setSelectedBrand(expectedBrand)
      setErrorMessage(`You must use ${expectedBrand} brand for this email.`)
      setIsLoading(false)
      return
    }

    try {
      const userCred = await signInWithEmailAndPassword(
        auth,
        email.trim().toLowerCase(),
        password
      )
      const user = userCred.user

    if (email.trim().toLowerCase() === "office@altrange.com") {
  const needsTwoFA = await checkTwoFARequired(user.uid)

  if (needsTwoFA) {
    setUserCredential(userCred)
    setShowTwoFA(true)
    setIsLoading(false)
    return
  }
}

      await completeLogin(userCred, expectedBrand)
    } catch (error) {
      console.error("Login error:", error)
      switch (error.code) {
        case "auth/user-not-found":
          setErrorMessage("User not found. Please check your email.")
          break
        case "auth/wrong-password":
          setErrorMessage("Incorrect password. Please try again.")
          break
        case "auth/too-many-requests":
          setErrorMessage("Too many login attempts. Please try again later.")
          break
        default:
          setErrorMessage("Login failed. Please try again.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const completeLogin = async (userCred, expectedBrand) => {
    const user = userCred.user
    if (expectedBrand === "super_admin") {
      localStorage.setItem("userToken", user.uid)
      localStorage.setItem("isAdmin", "true")
      localStorage.setItem("adminEmail", email.trim().toLowerCase())
      localStorage.removeItem("supplierBrand")
      localStorage.removeItem("supplierEmail")
      navigate("/admin-dashboard", { replace: true })
    } else {
      localStorage.setItem("userToken", user.uid)
      localStorage.setItem("supplierBrand", selectedBrand)
      localStorage.setItem("supplierEmail", email.trim().toLowerCase())
      localStorage.setItem("currentView", "welcome")
      localStorage.removeItem("isAdmin")
      localStorage.removeItem("adminEmail")
      navigate("/dashboard", { replace: true })
    }
  }

const sendOTP = async () => {
  setErrorMessage("");
  setSuccessMessage("");
  setOtp("");
  setIsLoading(true);  // SHOW PROCESSING

  if (!recaptchaVerifier) {
    setErrorMessage("reCAPTCHA not initialized. Please refresh the page.");
    setIsLoading(false);
    return;
  }

  try {
    const enteredPhone = phoneNumber.startsWith("+")
      ? phoneNumber
      : `+91${phoneNumber}`;

    if (enteredPhone !== ALLOWED_NUMBER) {
      setErrorMessage("This phone number is not registered for 2FA.");
      setIsLoading(false);
      return;
    }

    await recaptchaVerifier.render();

    const confirmation = await signInWithPhoneNumber(
      auth,
      enteredPhone,
      recaptchaVerifier
    );

    // OT​P SUCCESSFULLY SENT → NOW SHOW VERIFY SCREEN
    setConfirmationResult(confirmation);
    setIsOtpSent(true);

    setSuccessMessage("OTP sent successfully!");
    setTimer(RESEND_OTP_TIMEOUT);

  } catch (error) {
    setErrorMessage(error.message || "Failed to send OTP. Please try again.");
    setConfirmationResult(null);
    setIsOtpSent(false);
  } finally {
    setIsLoading(false);  // STOP SPINNER
  }
};

  const verifyOTP = async () => {
    setErrorMessage("")
    setSuccessMessage("")

    // If timer has expired, disallow verification
    if (timer <= 0) {
      setErrorMessage("OTP has expired. Please resend to receive a new code.")
      setConfirmationResult(null)
      setIsOtpSent(false)
      return
    }

    if (!otp || otp.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit OTP")
      return
    }

    if (!confirmationResult) {
      setErrorMessage("No active OTP session found. Please resend OTP.")
      setIsOtpSent(false)
      return
    }

    setIsLoading(true)
    try {
      // This will throw if OTP invalid or expired on server
      await confirmationResult.confirm(otp)
      // mark checked for today
      await markTwoFACompleted(userCredential.user.uid)
      const expectedBrand = brandMapping[email.trim().toLowerCase()]
      await completeLogin(userCredential, expectedBrand)
    } catch (error) {
      console.error("Error verifying OTP:", error)
      // If Firebase throws an expiration error, surface friendly msg
      if (error.code === "auth/invalid-verification-code") {
        setErrorMessage("Invalid OTP. Please try again.")
      } else if (error.code === "auth/code-expired") {
        setErrorMessage("OTP expired. Please resend to get a new code.")
        // ensure we invalidate local state too
        setConfirmationResult(null)
        setIsOtpSent(false)
        setTimer(0)
      } else {
        setErrorMessage(error.message || "Invalid OTP. Please try again.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const resetTwoFA = () => {
    setShowTwoFA(false)
    setPhoneNumber("")
    setOtp("")
    setIsOtpSent(false)
    setConfirmationResult(null)
    setUserCredential(null)
    setErrorMessage("")
    setSuccessMessage("")
    setTimer(0)

    // clear recaptcha to be safe
    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear()
        window.recaptchaVerifier = null
      }
      setRecaptchaVerifier(null)
    } catch (e) {
      console.error("Error clearing recaptcha:", e)
    }
  }

  return (
    <div className="login-container">
      <div className="login-background"></div>
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img src="/Images/logo.png" alt="Logo" />
          </div>
          <h1>{showTwoFA ? "Two-Factor Authentication" : "Supplier Login"}</h1>
          <p className="login-description">
            {showTwoFA
              ? !isOtpSent
                ? "Enter your phone number to receive a verification code"
                : "Enter the 6-digit code sent to your phone"
              : "Sign in with your registered email and password"}
          </p>
        </div>

        {showTwoFA ? (
          <div className="login-form">
            {!isOtpSent ? (
              <div className="input-group">
                <label className="input-label">
                  <FaPhone className="input-icon" /> Phone Number
                </label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    className="input"
                    placeholder="Enter phone number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        sendOTP()
                      }
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="input-group">
                <label htmlFor="otp" className="input-label">
                  <FaShieldAlt className="input-icon" /> Verification Code
                </label>
                <div className="input-wrapper">
                  <input
                    id="otp"
                    type="text"
                    className="input"
                    placeholder="Enter 6-digit code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    maxLength="6"
                    required
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        verifyOTP()
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {errorMessage && (
              <div className="error-message">
                <FaExclamationTriangle className="error-icon" />
                {errorMessage}
              </div>
            )}
            {successMessage && <div className="success-message">{successMessage}</div>}

        <button
  type="button"
  className="login-button"
  onClick={isOtpSent ? verifyOTP : sendOTP}
  disabled={isLoading || (isOtpSent && timer <= 0)}
>
  {isLoading ? (
    <>
      <span className="spinner" />
      <span>Processing...</span>
    </>
  ) : (
    <>
      <FaShieldAlt className="button-icon" />
      <span>{isOtpSent ? "Verify & Login" : "Send OTP"}</span>
    </>
  )}
</button>
            {isOtpSent && (
              <button
                type="button"
                className="login-button secondary"
                onClick={sendOTP}
                // only allow clicking resend after timer reaches 0
                disabled={isLoading || timer > 0}
              >
                {timer > 0 ? `Resend OTP in ${timer}s` : "Resend OTP"}
              </button>
            )}

            <button
              type="button"
              className="login-button secondary"
              onClick={resetTwoFA}
              disabled={isLoading}
            >
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleLogin} autoComplete="on" className="login-form">
            <div className="input-group">
              <label htmlFor="email" className="input-label">
                <FaUser className="input-icon" /> Email
              </label>
              <div className="input-wrapper">
                <input
                  id="email"
                  type="email"
                  className="input"
                  placeholder="Enter your email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="password" className="input-label">
                <FaLock className="input-icon" /> Password
              </label>
              <div className="input-wrapper">
                <input
                  id="password"
                  type="password"
                  className="input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            <div className="input-group hidden-select">
              <label htmlFor="brand" className="input-label">
                <FaBuilding className="input-icon" /> Brand
              </label>
              <div className="input-wrapper">
                <select
                  id="brand"
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  className={`input brand-select ${selectedBrand ? "selected" : ""}`}
                  required
                >
                  <option value="" disabled>
                    Select your brand
                  </option>
                  <option value="Ultratech">Ultratech</option>
                  <option value="Shree">Shree</option>
                  <option value="ACC">ACC</option>
                  <option value="JSW">JSW</option>
                  <option value="JKSuper">JKSuper</option>
                  <option value="Ambuja">Ambuja</option>
                </select>
              </div>
            </div>

            {errorMessage && (
              <div className="error-message">
                <FaExclamationTriangle className="error-icon" />
                {errorMessage}
              </div>
            )}

            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner" />
                  <span>Logging in...</span>
                </>
              ) : (
                <>
                  <FaSignInAlt className="button-icon" />
                  <span>Log In</span>
                </>
              )}
            </button>
          </form>
        )}

        {!showTwoFA && (
          <div className="login-footer">
            <p className="help-text">
              Need help? <a href="/contact-support">Contact Support</a>
            </p>
          </div>
        )}
      </div>

      <div id="recaptcha-container"></div>
    </div>
  )
}

export default Login
